import { useEffect, useMemo, useState } from "react";
import {
   Wallet,
  TrendingUp,
  ShoppingBag,
  Package,
  RefreshCw,
  Calendar,
  Truck,
  Receipt,
  BadgeDollarSign,
  DollarSign,
  Loader2,
} from "lucide-react";
import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";

import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";

import PageWrapper from "../components/PageWrapper";
import { apiFetch } from "../utils/apiFetch";

const COLORS = [
  "#6366f1",
  "#3b82f6",
  "#06b6d4",
  "#22c55e",
  "#f59e0b",
  "#ef4444",
];

export default function SettlementDashboard() {
  const [data, setData] = useState([]);

  const [loading, setLoading] = useState(false);
const [searchOrderId, setSearchOrderId] = useState("");
  const [activeType, setActiveType] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
const [historyData, setHistoryData] = useState([]);
const [historyLoading, setHistoryLoading] = useState(false);

  const filteredData = useMemo(() => {
  if (!searchOrderId) return data;

  return data.filter((item) =>
    String(item.order_id || item.order_status ||item.biensi_type ||item.statement_type || item.trx_type || "")
      .toLowerCase()
      .includes(searchOrderId.toLowerCase())
  );
}, [data, searchOrderId]);

  const getDefaultDateRange = () => {
  const today = new Date();

  const firstDay = new Date(
    today.getFullYear(),
    today.getMonth(),
    1
  );

  const format = (date) =>
    date.toISOString().split("T")[0];

  return {
    from: format(firstDay),
    to: format(today),
  };
};

const defaultDate = getDefaultDateRange();

const [dateFrom, setDateFrom] = useState(
  defaultDate.from
);

const [dateTo, setDateTo] = useState(
  defaultDate.to
);

  const formatRupiah = (num) =>
    new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      maximumFractionDigits: 0,
    }).format(num || 0);

  const fetchData = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");
 const kode_toko = localStorage.getItem("kode_toko");
      const params = new URLSearchParams();
      params.append("date_from", dateFrom);
      params.append("date_to", dateTo);
      params.append("kode_toko", kode_toko);

      const res = await apiFetch(
        `/api/tiktokshop/settlement-dashboard?${params.toString()}`,
        {
          headers: {
            "x-api-key": "SECRET123456",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const json = await res.json();

      setData(json.data || []);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

 useEffect(() => {
  fetchData();
}, [dateFrom, dateTo]);


const openHistory = async (lineItemId) => {
  try {
    setHistoryLoading(true);
  const token = localStorage.getItem("token");
 const kode_toko = localStorage.getItem("kode_toko");
    
      

  const response = await apiFetch(
  `/api/tiktokshop/line-item-history/${lineItemId}`,
  {
    headers: {
      "x-api-key": "SECRET123456",
      Authorization: `Bearer ${token}`,
    },
  }
);

const { data } = await response.json();

setHistoryData(data || []);
setShowHistoryModal(true);

  } catch (err) {
    console.error(err);
  } finally {
    setHistoryLoading(false);
  }
};

const exportToExcel = () => {
  const excelData = data.map((item) => {
    const totalCost =
      Math.abs(Number(item.shipping_cost_amount || 0)) +
      Math.abs(Number(item.fee_tax_amount || 0));

    const bfd = Number(
      item.subtotal_before_discount_amount || 0
    );

    const costPercent =
      bfd > 0 ? (totalCost / bfd) * 100 : 0;

    return {
     "Create Time": item.create_time
  ? item.create_time.replace("T", " ").substring(0, 19)
  : "",

      "Order ID": item.order_id,

      "Line Item ID": item.line_item_id,

      "Type Process": item.biensi_type,

      "Type Sales": item.sales_type,

      Status: item.order_status,

      SKU: item.seller_sku,

      Qty: Number(item.quantity || 0),

      Payment: Number(item.payment_amount || 0),

      "Tarik Dana": Number(
        item.withdraw_amount || 0
      ),

      "Subtotal BFD": Number(
        item.subtotal_before_discount_amount || 0
      ),

      "Seller Discount": Number(
        item.seller_discount_amount || 0
      ),

      Revenue: Number(
        item.rv_per_sku || 0
      ),

      Settlement: Number(
        item.settle_per_sku || 0
      ),

      "Shipping Cost": Math.abs(
        Number(item.shipping_cost_amount || 0)
      ),

      "Fee Tax": Math.abs(
        Number(item.fee_tax_amount || 0)
      ),

      "Total Cost": totalCost,

      "Cost %": Number(
        costPercent.toFixed(2)
      ),
    };
  });

  const worksheet =
    XLSX.utils.json_to_sheet(excelData);

  const workbook =
    XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    "Settlement"
  );

  // ===== HEADER STYLE =====
  const headerStyle = {
    font: {
      bold: true,
      color: { rgb: "FFFFFF" },
    },
    fill: {
      fgColor: { rgb: "1F4E78" },
    },
    alignment: {
      horizontal: "center",
      vertical: "center",
    },
    border: {
      top: { style: "thin", color: { rgb: "CCCCCC" } },
      bottom: { style: "thin", color: { rgb: "CCCCCC" } },
      left: { style: "thin", color: { rgb: "CCCCCC" } },
      right: { style: "thin", color: { rgb: "CCCCCC" } },
    },
  };

  // ===== CELL STYLE =====
  const cellStyle = {
    border: {
      top: { style: "thin", color: { rgb: "DDDDDD" } },
      bottom: { style: "thin", color: { rgb: "DDDDDD" } },
      left: { style: "thin", color: { rgb: "DDDDDD" } },
      right: { style: "thin", color: { rgb: "DDDDDD" } },
    },
    alignment: {
      vertical: "center",
    },
  };

  const range = XLSX.utils.decode_range(
    worksheet["!ref"]
  );

  // Apply styles
  for (let R = range.s.r; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({
        r: R,
        c: C,
      });

      if (!worksheet[cellAddress]) continue;

      // Header row
      if (R === 0) {
        worksheet[cellAddress].s = headerStyle;
      } else {
        worksheet[cellAddress].s = cellStyle;

        // Number formatting
        const header =
          excelData[0] &&
          Object.keys(excelData[0])[C];

        if (
          [
            "Payment",
            "Tarik Dana",
            "Subtotal BFD",
            "Seller Discount",
            "Revenue",
            "Settlement",
            "Shipping Cost",
            "Fee Tax",
            "Total Cost",
          ].includes(header)
        ) {
          worksheet[cellAddress].z =
            '#,##0';
        }

        if (header === "Cost %") {
          worksheet[cellAddress].z =
            '0.00"%"';
        }
      }
    }
  }

  // ===== AUTO WIDTH =====
  worksheet["!cols"] = Object.keys(
    excelData[0] || {}
  ).map((key) => ({
    wch: Math.max(key.length + 5, 18),
  }));

  // ===== FREEZE HEADER =====
  worksheet["!freeze"] = {
    xSplit: 0,
    ySplit: 1,
  };

  // ===== AUTO FILTER =====
  worksheet["!autofilter"] = {
    ref: worksheet["!ref"],
  };

  const excelBuffer = XLSX.write(
    workbook,
    {
      bookType: "xlsx",
      type: "array",
    }
  );

  const fileData = new Blob(
    [excelBuffer],
    {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }
  );

  saveAs(
    fileData,
    `settlement-${dateFrom}-${dateTo}.xlsx`
  );
};
const summary = useMemo(() => {
  const paymentGrouped = new Map();
  const withdrawGrouped = new Map();

  data.forEach((item) => {
    const key = item.id;

    if (!paymentGrouped.has(key)) {
      paymentGrouped.set(
        key,
        Number(item.payment_amount || 0)
      );
    }
  });

    data.forEach((item) => {
    const key = item.withdraw_id;

    if (!withdrawGrouped.has(key)) {
      withdrawGrouped.set(
        key,
        Number(item.withdraw_amount || 0)
      );
    }
  });

  const totalShippingCost = data.reduce(
    (a, b) =>
      a +
      Math.abs(
        Number(b.shipping_cost_amount || 0)
      ),
    0
  );

  const totalFeeTax = data.reduce(
    (a, b) =>
      a +
      Math.abs(
        Number(b.fee_tax_amount || 0)
      ),
    0
  );

  return {
    totalBFD: data.reduce(
  (a, b) =>
    a +
    Number(
      b.subtotal_before_discount_amount || 0
    ),
  0
),

    totalPayment: [...paymentGrouped.values()].reduce(
      (a, b) => a + b,
      0
    ),

    totalWithdraw: [...withdrawGrouped.values()].reduce(
      (a, b) => a + b,
      0
    ),

    totalRevenue: data.reduce(
      (a, b) => a + Number(b.rv_per_sku || 0),
      0
    ),

    totalSettlement: data.reduce(
      (a, b) => a + Number(b.settle_per_sku || 0),
      0
    ),

    totalQty: data.reduce(
      (a, b) => a + Number(b.quantity || 0),
      0
    ),

    totalShippingCost,

    totalFeeTax,

    totalCost:
      totalShippingCost + totalFeeTax,

    totalRows: data.length,

    totalOrder: new Set(
      data.map((d) => d.order_id)
    ).size,
  };
}, [data]);

  const chartData = useMemo(() => {
    const grouped = {};

    data.forEach((item) => {
      const date = item.create_time.replace("T", " ").substring(0, 19);

      if (!grouped[date]) {
        grouped[date] = {
          date,
          payment: 0,
          revenue: 0,
          settlement: 0,
        };
      }

      grouped[date].payment += Number(
        item.settle_per_order || 0
      );

      grouped[date].revenue += Number(
        item.rv_per_order || 0
      );

      grouped[date].settlement += Number(
        item.settle_per_order || 0
      );
    });

    return Object.values(grouped);
  }, [data]);

  const typeData = useMemo(() => {
    const grouped = {};

    data.forEach((item) => {
      const type = item.order_status || "UNKNOWN";

      if (!grouped[type]) {
        grouped[type] = {
          name: type,
          value: 0,
        };
      }

      grouped[type].value += Number(
        item.settle_per_order || 0
      );
    });

    return Object.values(grouped);
  }, [data]);

const stats = [
  {
    label: "Tarik Dana",
    description:
      "Total dana yang berhasil ditarik ke rekening",
    value: formatRupiah(summary.totalWithdraw),
    percent:
      summary.totalRevenue > 0
        ? `${(
            (summary.totalWithdraw /
              summary.totalRevenue) *
            100
          ).toFixed(1)}%`
        : null,
    icon: Wallet,
    color: "from-emerald-500 to-green-600",
    bg: "bg-emerald-50",
  },

  {
    label: "Total Payment",
    description:
      "Total pembayaran TikTok ke seller",
    value: formatRupiah(summary.totalPayment),
    percent:
      summary.totalRevenue > 0
        ? `${(
            (summary.totalPayment /
              summary.totalRevenue) *
            100
          ).toFixed(1)}%`
        : null,
    icon: Wallet,
    color: "from-indigo-500 to-blue-500",
    bg: "bg-indigo-50",
  },

  {
    label: "Subtotal BFD",
    description:
      "Nilai bruto produk sebelum diskon",
    value: formatRupiah(summary.totalBFD),
    percent:
      summary.totalRevenue > 0
        ? `${(
            (summary.totalBFD /
              summary.totalRevenue) *
            100
          ).toFixed(1)}%`
        : null,
    icon: DollarSign,
    color: "from-violet-500 to-fuchsia-500",
    bg: "bg-violet-50",
  },

  {
    label: "Revenue",
    description:
      "Total pendapatan bersih transaksi",
    value: formatRupiah(summary.totalRevenue),
    percent: "100%",
    icon: TrendingUp,
    color: "from-green-500 to-emerald-500",
    bg: "bg-green-50",
  },

  {
    label: "Settlement",
    description:
      "Total settlement order yang diterima",
    value: formatRupiah(summary.totalSettlement),
    percent:
      summary.totalRevenue > 0
        ? `${(
            (summary.totalSettlement /
              summary.totalRevenue) *
            100
          ).toFixed(1)}%`
        : null,
    icon: ShoppingBag,
    color: "from-orange-500 to-yellow-500",
    bg: "bg-orange-50",
  },

  {
    label: "Shipping Cost",
    description:
      "Total biaya pengiriman",
    value: formatRupiah(summary.totalShippingCost),
    percent:
      summary.totalRevenue > 0
        ? `${(
            (summary.totalShippingCost /
              summary.totalRevenue) *
            100
          ).toFixed(1)}%`
        : null,
    icon: Truck,
    color: "from-cyan-500 to-sky-500",
    bg: "bg-cyan-50",
  },

  {
    label: "Fee Tax",
    description:
      "Total fee platform & pajak",
    value: formatRupiah(summary.totalFeeTax),
    percent:
      summary.totalRevenue > 0
        ? `${(
            (summary.totalFeeTax /
              summary.totalRevenue) *
            100
          ).toFixed(1)}%`
        : null,
    icon: Receipt,
    color: "from-red-500 to-rose-500",
    bg: "bg-rose-50",
  },

  {
    label: "Total Cost",
    description:
      "Akumulasi seluruh potongan biaya",
    value: formatRupiah(summary.totalCost),
    percent:
      summary.totalRevenue > 0
        ? `${(
            (summary.totalCost /
              summary.totalRevenue) *
            100
          ).toFixed(1)}%`
        : null,
    icon: BadgeDollarSign,
    color: "from-amber-500 to-orange-500",
    bg: "bg-amber-50",
  },
];

  return (
    <PageWrapper>
      {/* HEADER */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">

        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            TikTok Settlement Finance
          </h1>

          <p className="text-sm text-gray-500 mt-1">
            Monitor settlement, order & revenue performance
          </p>
        </div>

        {/* FILTER */}
        <div className="flex flex-col sm:flex-row gap-3">

          <div className="flex items-center gap-2 bg-white border rounded-xl px-3 py-2 shadow-sm">
            <Calendar size={16} className="text-gray-400" />

            <input
              type="date"
              value={dateFrom}
              onChange={(e) =>
                setDateFrom(e.target.value)
              }
              className="outline-none text-sm"
            />
          </div>

          <div className="flex items-center gap-2 bg-white border rounded-xl px-3 py-2 shadow-sm">
            <Calendar size={16} className="text-gray-400" />

            <input
              type="date"
              value={dateTo}
              onChange={(e) =>
                setDateTo(e.target.value)
              }
              className="outline-none text-sm"
            />
          </div>

          <button
  onClick={fetchData}
  disabled={loading}
  className={`
    px-5 py-2 rounded-xl
    text-white text-sm font-medium
    transition flex items-center gap-2
    ${
      loading
        ? "bg-indigo-400 cursor-not-allowed"
        : "bg-indigo-600 hover:bg-indigo-700"
    }
  `}
>
  <RefreshCw
    size={16}
    className={loading ? "animate-spin" : ""}
  />

  {loading ? "Loading..." : "Filter"}
</button>

          <button
  onClick={exportToExcel}
  disabled={loading || data.length === 0}
  className={`
    px-5 py-2 rounded-xl
    text-white text-sm font-medium
    transition flex items-center gap-2
    ${
      loading || data.length === 0
        ? "bg-green-400 cursor-not-allowed"
        : "bg-green-600 hover:bg-green-700"
    }
  `}
>
  Export Excel
</button>

        </div>
      </div>

      {/* STATS */}
     <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-8 gap-5">

  {loading
    ? Array.from({ length: 8 }).map((_, i) => (
       <div
  key={i}
  className="
    bg-white rounded-3xl
    p-5 border border-gray-100
    shadow-sm hover:shadow-lg
    transition-all duration-300
    hover:-translate-y-1
  "
>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="h-3 w-20 bg-gray-200 rounded mb-3" />

              <div className="h-6 w-28 bg-gray-300 rounded" />
            </div>

            <div className="w-12 h-12 rounded-xl bg-gray-200" />
          </div>
        </div>
      ))

    : stats.map((s, i) => {
        const Icon = s.icon;

        return (
          <div
            key={i}
            className="bg-white rounded-2xl p-5 border shadow-sm hover:shadow-md transition"
          >
           <div className="flex items-start justify-between gap-3">

  <div className="flex-1 min-w-0">

    <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
      {s.label}
    </p>

    <h2 className="text-lg font-bold text-gray-800 mt-2 leading-tight">
      {s.value}
    </h2>

    {s.percent && (
      <div className="mt-3 inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1">
        <span className="text-[11px] font-semibold text-gray-700">
          {s.percent} dari revenue
        </span>
      </div>
    )}
  </div>

  <div
    className={`
      w-10 h-10 rounded-2xl
      flex items-center justify-center
      bg-gradient-to-br ${s.color}
      text-white shadow-sm
      shrink-0
    `}
  >
    <Icon size={16} />
  </div>

</div>
          </div>
        );
      })}
</div>

      {/* MAIN GRID */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mt-6">

        {/* CHART */}
        <div className="xl:col-span-2 bg-white rounded-2xl border shadow-sm p-5">

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-5">

            <div>
              <h3 className="font-semibold text-gray-700">
                Settlement Trend
              </h3>

              <p className="text-xs text-gray-400 mt-1">
                Daily settlement overview
              </p>
            </div>

          </div>

          <div className="relative">
  {loading && (
    <div className="absolute inset-0 z-10 bg-white/70 backdrop-blur-[1px] flex items-center justify-center rounded-xl">
      <div className="flex items-center gap-2 text-indigo-600">
        <Loader2
          size={18}
          className="animate-spin"
        />

        <span className="text-sm font-medium">
          Loading chart...
        </span>
      </div>
    </div>
  )}

          <ResponsiveContainer width="100%" height={350}>

            <AreaChart data={chartData}>

              <defs>

                <linearGradient
                  id="payment"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor="#6366f1"
                    stopOpacity={0.4}
                  />

                  <stop
                    offset="95%"
                    stopColor="#6366f1"
                    stopOpacity={0}
                  />
                </linearGradient>

              </defs>

              <CartesianGrid
                stroke="#f1f5f9"
                strokeDasharray="3 3"
              />

              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
              />

              <YAxis
                tickFormatter={(v) =>
                  `${(v / 1000000).toFixed(0)}M`
                }
                tick={{ fontSize: 11 }}
              />

              <Tooltip
                formatter={(v) =>
                  formatRupiah(v)
                }
              />

              <Area
                type="monotone"
                dataKey="payment"
                stroke="#6366f1"
                fill="url(#payment)"
                strokeWidth={3}
              />

            </AreaChart>

          </ResponsiveContainer>
</div>
        </div>

        {/* PIE */}
        <div className="bg-white rounded-2xl border shadow-sm p-5">

          <div className="flex items-center justify-between mb-5">

            <div>
              <h3 className="font-semibold text-gray-700">
                Statement Type
              </h3>

              <p className="text-xs text-gray-400 mt-1">
                Revenue contribution
              </p>
            </div>

          </div>

          <ResponsiveContainer width="100%" height={300}>

            <PieChart>

              <Pie
                data={typeData}
                dataKey="value"
                nameKey="name"
                innerRadius={70}
                outerRadius={100}
                paddingAngle={4}
                onMouseEnter={(_, index) =>
                  setActiveType(typeData[index].name)
                }
              >

                {typeData.map((entry, i) => (
                  <Cell
                    key={i}
                    fill={COLORS[i % COLORS.length]}
                    opacity={
                      activeType &&
                      activeType !== entry.name
                        ? 0.3
                        : 1
                    }
                  />
                ))}

              </Pie>

              <Tooltip
                formatter={(v) =>
                  formatRupiah(v)
                }
              />

            </PieChart>

          </ResponsiveContainer>

          <div className="space-y-2 mt-3">

            {typeData.map((item, i) => (
              <div
                key={i}
                className="flex items-center justify-between"
              >

                <div className="flex items-center gap-2">

                  <div
                    className="w-3 h-3 rounded-full"
                    style={{
                      background:
                        COLORS[i % COLORS.length],
                    }}
                  />

                  <span className="text-sm text-gray-600">
                    {item.name}
                  </span>

                </div>

                <span className="text-xs text-gray-500">
                  {formatRupiah(item.value)}
                </span>

              </div>
            ))}

          </div>

        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-2xl border shadow-sm p-5 mt-6">

        <div className="flex items-center justify-between mb-5">

          <div>
            <h3 className="font-semibold text-gray-700">
              Settlement Detail
            </h3>

            <p className="text-xs text-gray-400 mt-1">
              Detailed order settlement data
            </p>
          </div>

          <div className="relative w-full lg:w-80">

  <input
    type="text"
    placeholder="Search Order ID..."
    value={searchOrderId}
    onChange={(e) =>
      setSearchOrderId(e.target.value)
    }
    className="
      w-full rounded-2xl
      border border-gray-200
      bg-gray-50
      px-4 py-3
      text-sm
      outline-none
      focus:ring-2
      focus:ring-indigo-500
      focus:border-indigo-500
      transition
    "
  />
  <p className="text-xs text-gray-400 mt-2">
  {filteredData.length.toLocaleString("id-ID")} results
</p>

</div>

        </div>

        <div className="overflow-auto">
  <table className="w-full text-sm">
    <thead>
      <tr className="border-b bg-gray-50">
        <th className="text-left p-3">Tanggal WD</th>
        <th className="text-left p-3">Order ID</th>
        <th className="text-left p-3">Type</th>
        <th className="text-left p-3">Status</th>
        <th className="text-left p-3">SKU</th>
        <th className="text-right p-3">Qty</th>
        <th className="text-right p-3">Payment</th>
        <th className="text-right p-3">Subtotal BFD</th>
        <th className="text-right p-3">Seller Discount</th>
        <th className="text-right p-3">Revenue</th>
        <th className="text-right p-3">Settlement</th>
        <th className="text-right p-3">Shipping Cost</th>
        <th className="text-right p-3">Fee Tax</th>
        <th className="text-right p-3">Total Cost</th>
        <th className="text-right p-3">Cost % (vs BFD)</th>
      </tr>
    </thead>

  <tbody>
  {filteredData.map((item, i) => {
    const currentId = String(item.id).trim();

    const nextItem = data[i + 1];
    const nextId = nextItem ? String(nextItem.id).trim() : null;

    const isGroupEnd = currentId !== nextId;

    return (
      <tr
        key={`${currentId}-${i}`}
        className={`
          transition hover:bg-gray-50
          border-b
          ${isGroupEnd ? "border-b-2 border-red-500" : "border-gray-100"}
        `}
      >
        <td className="p-3 text-gray-600">
          {item.create_time?.replace("T", " ").substring(0, 19)}
        </td>

      
        <td className="p-3 text-gray-700">{item.order_id}</td>
         <td className="p-3 text-gray-700">
       <button
  onClick={() => openHistory(item.line_item_id)}
  className={`text-xs font-medium px-2 py-1 rounded-md hover:opacity-80 transition
    ${
      item.biensi_type === "P O"
        ? "bg-green-50 text-green-600"
        : item.biensi_type === "M O"
        ? "bg-yellow-50 text-yellow-600"
        : item.biensi_type === "N O"
        ? "bg-red-50 text-red-600"
        : item.biensi_type === "P R"
        ? "bg-blue-50 text-blue-600"
        : item.biensi_type === "M R"
        ? "bg-purple-50 text-purple-600"
        : item.biensi_type === "N R"
        ? "bg-orange-50 text-orange-600"
        : "bg-gray-100 text-gray-600"
    }
  `}
>
  {item.biensi_type}
  
   <span
    className={`text-xs font-medium px-2 py-1 rounded-md
      ${
        item.sales_type === "C"
          ? "bg-green-50 text-green-600"
          : item.sales_type === "O"
          ? "bg-yellow-50 text-yellow-600"
          : "bg-gray-100 text-gray-600"
      }
    `}
  >
    {item.sales_type || "UNKNOWN"}
  </span>
</button>
       </td>

        <td className="p-3">
  <span className={`text-xs font-medium px-2 py-1 rounded-md
    ${
      item.order_status === "COMPLETED"
        ? "bg-green-50 text-green-600"
        :item.order_status === "DELIVERED"
        ? "bg-green-50 text-green-600"
        : item.order_status === "CANCELLED"
        ? "bg-red-50 text-red-600"
        : item.order_status === "CANCEL"
        ? "bg-red-50 text-red-600"
        : item.order_status === "PENDING"
        ? "bg-yellow-50 text-yellow-600"
        : item.order_status === "RETURN"
        ? "bg-blue-50 text-blue-600"
        : "bg-gray-100 text-gray-600"
    }
  `}>
    {item.order_status || "UNKNOWN"}
  </span>
</td>

        <td className="p-3 text-gray-700">{item.seller_sku}</td>

        <td className="p-3 text-right">{item.quantity}</td>

        <td className="p-3 text-right text-gray-800">
          {formatRupiah(item.payment_amount)}
        </td>

        <td className="p-3 text-right text-gray-800">
          {formatRupiah(item.subtotal_before_discount_amount)}
        </td>

        <td className="p-3 text-right text-gray-800">
          {formatRupiah(item.seller_discount_amount)}
        </td>

        <td className="p-3 text-right text-gray-800">
          {formatRupiah(item.rv_per_sku)}
        </td>

        <td className="p-3 text-right text-gray-800">
          {formatRupiah(item.settle_per_sku)}
        </td>

         <td className="p-3 text-right text-gray-800">
          {formatRupiah(item.shipping_cost_amount)}
        </td>
         <td className="p-3 text-right text-gray-800">
          {formatRupiah(item.fee_tax_amount)}
        </td>
          <td className="p-3 text-right text-gray-800 font-semibold">
          {formatRupiah(
              Number(item.shipping_cost_amount || 0) +
              Number(item.fee_tax_amount || 0)
          )}

        </td>
        <td className="p-3 text-right font-semibold">
  {(() => {
    const totalCost =
      Number(item.shipping_cost_amount || 0) +
      Number(item.fee_tax_amount || 0);

    const bfd = Number(item.subtotal_before_discount_amount || 0);

    const percent =
      bfd > 0 ? (totalCost / bfd) * 100 : 0;

    return (
      <span
        className={
          percent > 20
            ? "text-red-600"
            : percent > 10
            ? "text-yellow-600"
            : "text-green-600"
        }
      >
        {percent.toFixed(2)}%
      </span>
    );
  })()}
</td>

      </tr>
    );
  })}
</tbody>
  </table>
</div>

      {!loading && filteredData.length === 0 && (
          <div className="text-center py-10 text-sm text-gray-400">
            No settlement data found
          </div>
        )}

     {showHistoryModal && (
  <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">

    <div className="bg-white rounded-3xl w-[900px] max-h-[85vh] overflow-auto p-6">

      <div className="flex justify-between items-center mb-4">
        <h2 className="font-bold text-lg">
          History Product Order SKU: {historyData[0]?.line_item_id || "-"}
        </h2>

        <button
          onClick={() => setShowHistoryModal(false)}
          className="text-gray-500"
        >
          ✕
        </button>
      </div>

      {historyLoading ? (
        <div className="text-center py-10">
          Loading...
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="p-3 text-left">Tanggal</th>
              <th className="p-3 text-left">Source</th>
              <th className="p-3 text-left">PIC</th>
              <th className="p-3 text-left">Lokasi</th>
              <th className="p-3 text-left">Inv Status</th>
            </tr>
          </thead>

          <tbody>
            {historyData.map((row, idx) => (
              <tr
                key={idx}
                className="border-b hover:bg-gray-50"
              >
                <td className="p-3">
                  {row.tanggal.replace("T", " ").substring(0, 19)}
                </td>

                <td className="p-3">
                  <span className="px-2 py-1 rounded bg-indigo-50 text-indigo-600 text-xs">
                    {row.source}
                  </span>
                </td>

                <td className="p-3">
                  {row.pic}
                </td>

                <td className="p-3">
                  {row.lokasi}
                </td>
                <td className="p-3">
                  {row.inv_status}
                </td>
              </tr>
            ))}
          </tbody>

        </table>
      )}

    </div>

  </div>
)}

      </div>
    </PageWrapper>
  );
}